import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, FileDown, Send, ArrowRightLeft, Check, X } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { money, StatusBadge, ItemsEditor } from "./billingCommon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

export default function Estimates() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["owner", "admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const { data: estimates } = useQuery({ queryKey: ["estimates"], queryFn: () => api.get("/estimates").then(r => r.data.estimates) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["estimates"] });
  const remove = async (e) => { await api.delete(`/estimates/${e.id}`); refresh(); toast.success("Estimate deleted"); };

  const setStatus = async (id, status) => { try { await api.put(`/estimates/${id}/status`, null, { params: { status } }); refresh(); toast.success("Updated"); } catch (e) { toast.error("Failed"); } };
  const convert = async (id) => {
    try { await api.post(`/estimates/${id}/convert`); refresh(); qc.invalidateQueries({ queryKey: ["invoices"] }); toast.success("Converted to invoice"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Estimates" subtitle="Draft estimates stay drafts until approved" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setOpen(true)} data-testid="new-estimate-btn"><Plus className="h-4 w-4" /> New Estimate</Button>
      } />
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left p-3">Number</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Total</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {(estimates || []).length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-slate-400">No estimates yet.</td></tr> :
              (estimates || []).map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`estimate-row-${e.id}`}>
                  <td className="p-3 font-mono text-xs text-slate-500">{e.estimateNumber}</td>
                  <td className="p-3 font-medium text-navy">{e.customerName}<span className="block text-xs text-slate-400">{e.customerEmail}</span></td>
                  <td className="p-3 font-medium text-navy">{money(e.total)}</td>
                  <td className="p-3"><StatusBadge status={e.status} /></td>
                  <td className="p-3"><div className="flex gap-1 justify-end">
                    {e.status === "draft" && <button title="Mark sent" onClick={() => setStatus(e.id, "sent")} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`send-estimate-${e.id}`}><Send className="h-4 w-4" /></button>}
                    {(e.status === "sent" || e.status === "draft") && <button title="Approve" onClick={() => setStatus(e.id, "approved")} className="p-1.5 rounded hover:bg-green-50 text-green-600"><Check className="h-4 w-4" /></button>}
                    {e.status === "sent" && <button title="Reject" onClick={() => setStatus(e.id, "rejected")} className="p-1.5 rounded hover:bg-red-50 text-red-500"><X className="h-4 w-4" /></button>}
                    {!e.invoiceId && <button title="Convert to invoice" onClick={() => convert(e.id)} className="p-1.5 rounded hover:bg-electric/10 text-electric" data-testid={`convert-estimate-${e.id}`}><ArrowRightLeft className="h-4 w-4" /></button>}
                    <a href={`${API}/estimates/${e.id}/pdf`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><FileDown className="h-4 w-4" /></a>
                    {canDelete && <ConfirmDelete onConfirm={() => remove(e)} title="Delete this estimate?" description={`Estimate ${e.estimateNumber} for ${e.customerName} will be removed.`} testid={`estimate-delete-${e.id}`} />}
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {open && <EstimateDialog onClose={() => setOpen(false)} onSaved={() => { refresh(); setOpen(false); }} />}
    </div>
  );
}

function EstimateDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ customerName: "", customerEmail: "", taxRate: 0, discount: 0, notes: "" });
  const [items, setItems] = useState([{ name: "", description: "", quantity: 1, unitPrice: 0 }]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/estimates", { ...form, taxRate: Number(form.taxRate), discount: Number(form.discount), items: items.filter(i => i.name) }); toast.success("Estimate created"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const subtotal = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>New Estimate</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="estimate-form">
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Customer name" required value={form.customerName} onChange={e => set("customerName", e.target.value)} data-testid="est-name" />
          <input className={input} type="email" placeholder="Customer email" required value={form.customerEmail} onChange={e => set("customerEmail", e.target.value)} data-testid="est-email" />
        </div>
        <ItemsEditor items={items} setItems={setItems} />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-600">Tax %<input className={input} type="number" value={form.taxRate} onChange={e => set("taxRate", e.target.value)} data-testid="est-tax" /></label>
          <label className="text-sm text-slate-600">Discount $<input className={input} type="number" value={form.discount} onChange={e => set("discount", e.target.value)} /></label>
        </div>
        <textarea className={`${input} h-auto py-2`} rows={2} placeholder="Notes" value={form.notes} onChange={e => set("notes", e.target.value)} />
        <p className="text-sm text-slate-500 text-right">Subtotal: <span className="font-semibold text-navy">{money(subtotal)}</span></p>
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="est-save">Create Estimate</Button>
      </form>
    </DialogContent></Dialog>
  );
}
